import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { EMPTY_LIST } from '../../../../../constants/common'
import {
  useContentOverflow,
  useDateLocale,
  useTimeZone,
} from '../../../../../hooks'
import { getQaSelector } from '../../../../../utils/common'
import {
  isEventKeyEnter,
  isEventKeySpaceBar,
  preventDefault,
  stopPropagation,
} from '../../../../../utils/events'
import { ActionSelect, getActionSelectOptions } from '../../../../Action'
import Button, { BUTTON_HEIGHT_24 } from '../../../../Button'
import { Checkbox, CHECKBOX_SIZE, CHECKBOX_TYPE } from '../../../../Checkbox'
import { EntityArchivedIcon } from '../../../../Entity'
import { FloatingTooltip } from '../../../../Floating'
import { RentalPortalApplicantIdentityCell } from '../../../../RentalPortalApplicant'
import { Status } from '../../../../Status'
import Text from '../../../../Text'
import {
  getHasMatchScore,
  RentalPortalMatchCriteriaPopover,
  RentalPortalMatchEligibilityBadge,
  RentalPortalMatchFitCell,
} from '../../../RentalPortalMatch'
import { getApplicantWithHighlightBudget } from '../../RentalPortalApplicants.utils'
import {
  QA_NAME_APPLICANT_BOARD_CARD,
  QA_NAME_APPLICANT_BOARD_CARD_ACTIONS,
  QA_NAME_APPLICANT_BOARD_CARD_SELECT,
} from '../RentalPortalApplicantBoard.constants'

import {
  getApplicantBoardCardHandoff,
  getApplicantBoardCardMeta,
  getHasApplicantBoardCardHead,
} from './RentalPortalApplicantBoardCard.utils'

import * as styles from './RentalPortalApplicantBoardCard.module.css'

import Archive2Icon from '../../../../../icons/archive-2.svg'

function RentalPortalApplicantBoardCard({
  getActions,
  hasHandoff,
  isListingScoped,
  isSelectable,
  isSelected,
  item,
  selectNotAllowedTooltip,
  onCallAction,
  onClick,
  onSelect,
}) {
  const { archived, attention } = item

  const { t } = useTranslation()

  const locale = useDateLocale()

  const timezone = useTimeZone()

  const metaLabel = getApplicantBoardCardMeta({
    isListingScoped,
    item,
    locale,
    t,
    timezone,
  })

  const { overflow, setContentOverflowRef } = useContentOverflow({
    dep: [metaLabel],
  })

  const handoff = getApplicantBoardCardHandoff({ hasHandoff, item })

  const actions = getActionSelectOptions({
    actions: getActions?.(item) ?? EMPTY_LIST,
    t,
  })

  function handleClick(event) {
    onClick?.(item.id, event)
  }

  function handleKeyDown(event) {
    if (
      event.target === event.currentTarget &&
      (isEventKeyEnter(event) || isEventKeySpaceBar(event))
    ) {
      preventDefault(event)

      onClick?.(item.id, event)
    }
  }

  function handleClickAction(action) {
    onCallAction?.(action?.value, item)
  }

  const selectLabel = t('RentalPortal.BoardCardSelectKey')

  function handleSelect(isChecked) {
    onSelect?.(item, isChecked)
  }

  return (
    <div
      {...getQaSelector({ value: QA_NAME_APPLICANT_BOARD_CARD })}
      className={classNames(styles.RentalPortalApplicantBoardCard, {
        [styles.clickable]: Boolean(onClick),
      })}
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      {getHasApplicantBoardCardHead({ actions, archived, onSelect }) ? (
        <span className={styles.head}>
          {onSelect ? (
            <span onClick={stopPropagation}>
              <Checkbox
                checked={isSelected}
                disabled={!isSelectable}
                label={selectLabel}
                labelClassName={styles.selectLabel}
                qaName={QA_NAME_APPLICANT_BOARD_CARD_SELECT}
                size={CHECKBOX_SIZE.small}
                tooltip={isSelectable ? selectLabel : selectNotAllowedTooltip}
                type={CHECKBOX_TYPE.table}
                onChange={handleSelect}
              />
            </span>
          ) : null}
          <span className={styles.headActions}>
            {archived ? <EntityArchivedIcon Icon={Archive2Icon} /> : null}
            {actions.length > 0 ? (
              <span onClick={stopPropagation}>
                <ActionSelect
                  actions={actions}
                  controlProps={{
                    buttonProps: {
                      Button: Button.Text,
                      height: BUTTON_HEIGHT_24,
                      qaName: QA_NAME_APPLICANT_BOARD_CARD_ACTIONS,
                    },
                  }}
                  qaName={QA_NAME_APPLICANT_BOARD_CARD_ACTIONS}
                  onClickAction={handleClickAction}
                />
              </span>
            ) : null}
          </span>
        </span>
      ) : null}
      <span className={styles.identityRow}>
        <RentalPortalApplicantIdentityCell
          applicant={getApplicantWithHighlightBudget(item)}
          className={styles.identity}
        />
        {getHasMatchScore(item.match) ? (
          <RentalPortalMatchCriteriaPopover
            className={styles.fit}
            match={item.match}
          >
            <RentalPortalMatchFitCell isScoreOnly match={item.match} />
          </RentalPortalMatchCriteriaPopover>
        ) : null}
      </span>
      <FloatingTooltip content={metaLabel} disabled={!overflow}>
        <Text isSecondary className={styles.meta} ref={setContentOverflowRef}>
          {metaLabel}
        </Text>
      </FloatingTooltip>
      {item.match || handoff || attention?.needs_you ? (
        <span className={styles.signals}>
          {item.match ? (
            <RentalPortalMatchEligibilityBadge
              hasReason={false}
              match={item.match}
            />
          ) : null}
          {attention?.needs_you ? (
            <span className={styles.needsYou}>
              <span className={styles.marker} />
              {t(
                attention.primary_action?.label ||
                  'RentalPortal.ListingBoardNeedsYouKey'
              )}
            </span>
          ) : null}
          {handoff ? (
            <Status text={t(handoff.text)} textColor={handoff.textColor} />
          ) : null}
        </span>
      ) : null}
    </div>
  )
}

export default RentalPortalApplicantBoardCard
